export const TASKS_MODULE_INFO_TITLE = 'About tasks';
export const TASKS_MODULE_INFO_MARKDOWN = `
## What the tasks module is

The tasks module is Hana's operational work board. It is where work items are created, organized, updated, assigned, and tracked across their lifecycle.

## What the columns mean

- Not started: work that exists but has not begun yet.
- In progress: work that is actively being worked on.
- Completed: work that is finished.
- Backlog: work that is deferred, paused, or intentionally not active right now.

## What tasks are used for

- Tracking work ownership and current state.
- Capturing execution details such as title, description, assignees, priority, due date, tags, and story points.
- Recording progress updates and task-specific context in one place.
- Giving teams a shared operational view instead of relying on scattered messages.

## How to use this module well

- Create one task per clear unit of work.
- Keep task titles specific enough to scan quickly.
- Move tasks between columns when their real status changes.
- Use filters and search when the board gets large.
- Open task details when you need the full context or update history.

## What this module does not do

- It does not replace the source conversation where work was originally discussed.
- It does not automatically make vague work clear unless the task itself is written clearly.
- It does not guarantee execution just because a task exists. Owners, updates, and status changes still matter.

## When to use tasks

Use tasks when work needs explicit ownership, visible status, and a durable record of progress that the team can revisit later.
`;

export const OVERVIEW_MODULE_INFO_TITLE = 'About overview';
export const OVERVIEW_MODULE_INFO_MARKDOWN = `
## What the overview module is

The overview module is the dashboard home for the active organization. It shows the broad operating picture: usage, adoption, memory footprint, task volume, and recent activity patterns.

## What this module helps you answer

- Is Hana being used regularly by the team?
- Which capabilities are active in the current organization?
- Are tasks, memories, and integrated spaces growing as expected?
- Which periods or users show spikes or drops in activity?

## How to read the page

- Summary cards give fast totals for the current active organization.
- Charts help you spot usage trends over time.
- Command activity helps you understand which workflows people actually use.
- Sprint or activity summaries surface operational signals without opening deeper modules first.

## What this module does not do

- It does not replace the deeper modules where edits and configuration happen.
- It does not show every individual record behind the numbers.
- It does not merge data across multiple organizations. Everything follows the active org context.

## When to use overview

Use this module when you want a fast health check of adoption, operations, and organization-level activity before drilling into Memory, Tasks, Spaces, or Settings.
`;

export const MEMORY_MODULE_INFO_TITLE = 'About memory';
export const MEMORY_MODULE_INFO_MARKDOWN = `
## What the memory module is

The memory module is where your organization gives Hana durable knowledge to retrieve later during conversations.

## What lives here

- Text memories and uploaded knowledge sources.
- Connector-backed knowledge such as documents, spaces, and external systems.
- Batch status, ingestion progress, tags, and source-specific controls.

## What this module helps you manage

- Adding new knowledge sources.
- Deciding which source type fits a use case.
- Monitoring ingestion state and fixing failed batches.
- Organizing knowledge with tags, titles, and instructions.

## How to use this module well

- Pick the source type that matches where the truth already lives.
- Add clear titles so batches are easy to audit later.
- Use instructions only when retrieval needs extra framing.
- Revisit failed or stale ingestions instead of creating duplicate batches.

## What this module does not do

- It does not guarantee perfect answers if the source content itself is weak or outdated.
- It does not replace access management for the upstream systems being connected.
- It does not change live source data just because it was ingested here.

## When to use memory

Use this module when Hana should reliably answer from durable team knowledge instead of only the current conversation context.
`;

export const SPACES_MODULE_INFO_TITLE = 'About spaces';
export const SPACES_MODULE_INFO_MARKDOWN = `
## What the spaces module is

The spaces module shows the Google Chat spaces Hana can see for the current organization and the current setup state of each space.

## What a space represents

A space is a Google Chat room, threadable collaboration area, or shared conversation context where people work together. In Hana, spaces matter because they define where Hana can participate and where space-based capabilities are available.

## What this module helps you manage

- Whether Hana has been added to a space.
- Whether the space still needs setup.
- Whether Hana access is enabled for an added space.
- Whether historic chat memory has been ingested for that space.
- Whether ongoing tracking memory is active for that space.

## How to read the status

- Added: Hana is already present and the space is set up for active use.
- Needs setup: the space is known but still requires setup steps before Hana can fully operate there.

## What the memory indicators mean

- History ingested: a past snapshot of messages has already been added as memory.
- Tracking active: Hana is continuously tracking new content from that space over time.

## What this module does not do

- It does not itself contain all the messages from the space.
- It does not replace the memory module for detailed ingestion management.
- It does not replace Google Chat as the live collaboration surface.

## When to use spaces

Use this module when you need to understand where Hana is available, verify setup state, review which spaces have memory coverage, or enable and disable Hana access for added spaces.
`;

export const SETTINGS_MODULE_INFO_TITLE = 'About settings';
export const SETTINGS_MODULE_INFO_MARKDOWN = `
## What the settings module is

The settings module manages organization-level configuration, profile settings, and billing-related controls for the currently active organization.

## What this module helps you manage

- Organization details and shared configuration.
- Billing visibility and seat-related context.
- Profile-level settings that affect how people use Hana in the dashboard.

## How to approach changes here

- Treat settings as active-org scoped unless the page says otherwise.
- Review role permissions before assuming a control should be editable.
- Use this module for configuration, not for operational data entry.

## What this module does not do

- It does not switch your active organization for you.
- It does not backfill operational data into other modules.
- It does not replace membership management or connector setup flows.

## When to use settings

Use this module when you need to configure organization behavior, review billing-related state, or update profile and account-level preferences.
`;

export const USERS_MODULE_INFO_TITLE = 'About users';
export const USERS_MODULE_INFO_MARKDOWN = `
## What the users module is

The users module manages people in the currently active organization. It shows current members, invited users, and join requests depending on the selected tab and your role.

## What each area means

- Members: people who are already part of the organization.
- Invited users: people who have been invited but have not completed joining yet.
- Join requests: people who requested access and are waiting for review.

## What this module helps you manage

- Inviting new teammates.
- Reviewing who already has access.
- Updating roles where permitted.
- Removing users where appropriate.
- Reviewing and deciding organization join requests.

## Why this module matters

Organization membership controls who can access Hana inside the org context, who can collaborate in shared workflows, and which people can administer settings or operate organization-level features.

## What roles imply here

Roles determine what a user is allowed to see or change. This module is where admins and other permitted roles review that access model at the people level.

## What this module does not do

- It does not replace authentication or identity providers.
- It does not describe every action a user has taken inside the product.
- It does not automatically grant the right access model unless membership and roles are reviewed intentionally.

## When to use users

Use this module when onboarding teammates, auditing current membership, cleaning up stale access, or reviewing pending requests to join the organization.
`;

export const SAVED_PROMPTS_MODULE_INFO_TITLE = 'About saved prompts';
export const SAVED_PROMPTS_MODULE_INFO_MARKDOWN = `
## What the saved prompts module is

Saved prompts are reusable prompt templates shared inside the current organization so people do not have to rewrite the same instructions repeatedly.

## What this module helps you manage

- Creating prompts for recurring workflows.
- Standardizing prompt wording across the team.
- Updating prompts when process expectations change.
- Removing stale prompts that no longer reflect the best workflow.

## How to use saved prompts well

- Give each prompt a title that makes the use case obvious.
- Use the description to explain when the prompt should be used.
- Keep the actual prompt focused on one repeatable outcome.
- Prefer updating one shared prompt over creating many near-duplicates.

## What this module does not do

- It does not automatically run the prompt.
- It does not guarantee the prompt is correct for every edge case.
- It does not replace source knowledge in the memory module.

## When to use saved prompts

Use saved prompts when the team repeats the same prompt patterns often and you want a shared, durable starting point.
`;

export const SAVED_PROMPTS_MANAGEMENT_INFO_TITLE = 'Managing saved prompts';
export const SAVED_PROMPTS_MANAGEMENT_INFO_MARKDOWN = `
## What to do here

- Create a new saved prompt when a workflow is repeated often enough to justify standardization.
- Edit an existing saved prompt when the team already has a near-match and only the wording needs improvement.
- Delete prompts that are stale, duplicated, or misleading.

## Good prompt hygiene

- Keep titles short and obvious.
- Use descriptions to clarify intent, audience, or expected output.
- Avoid storing several prompts that solve the same thing with tiny wording differences.
`;

export const REPORT_GROUPS_MODULE_INFO_TITLE = 'About report groups';
export const REPORT_GROUPS_MODULE_INFO_MARKDOWN = `
## What the report groups module is

Report groups are named buckets for organizing operational reports, updates, incidents, or recurring message streams.

## Why report groups matter

They give related updates one durable home so teams can review history without mixing unrelated topics together.

## What this module helps you manage

- Creating a durable group for a workflow or topic.
- Renaming a group when the scope changes.
- Reviewing how many messages a group already contains.
- Jumping into the message history for a specific group.

## Naming guidance

- Use lowercase snake_case names.
- Prefer names that describe the ongoing topic, not a one-off event.
- Keep names stable so people can reference them consistently later.

## What this module does not do

- It does not replace the individual messages inside a group.
- It does not automatically classify updates unless they are created under the right group.
- It does not merge unrelated workstreams just because the titles are similar.

## When to use report groups

Use report groups when updates need a durable topic container that people can revisit over time.
`;

export const REPORT_GROUPS_MANAGEMENT_INFO_TITLE = 'Managing report groups';
export const REPORT_GROUPS_MANAGEMENT_INFO_MARKDOWN = `
## What to do here

- Create a report group before the first related message is posted.
- Reuse an existing group when the topic already exists.
- Edit the group name only when the scope truly changed.

## Naming rule

Use lowercase snake_case so the group is easy to reference consistently in workflows and automation.
`;

export const REPORT_MESSAGES_MODULE_INFO_TITLE = 'About report group messages';
export const REPORT_MESSAGES_MODULE_INFO_MARKDOWN = `
## What the report messages module is

This page shows the messages posted inside one specific report group.

## What this module helps you do

- Read the history for a focused topic.
- Add a new message to that topic.
- Copy links to specific updates.
- Edit or delete eligible messages within the allowed window.

## How to use it well

- Keep each message focused on one meaningful update.
- Use the group history to avoid repeating context unnecessarily.
- Copy direct links when you want to reference a specific update elsewhere.

## What this module does not do

- It does not replace the report group list itself.
- It does not allow indefinite editing of older messages.
- It does not turn unrelated updates into a coherent history unless they are posted to the correct group.

## When to use report messages

Use this page when you need the detailed update history for one report group or you need to add a new topic-specific update.
`;

export const REPORT_MESSAGES_MANAGEMENT_INFO_TITLE =
  'Managing report group messages';
export const REPORT_MESSAGES_MANAGEMENT_INFO_MARKDOWN = `
## What to do here

- Create a new message when there is a meaningful update for this report group.
- Use copy link when a specific update should be shared elsewhere.
- Use copy message when the content itself needs to be reused.
- Edit or delete only while the message is still within the permitted window.
`;

export const ORGANIZATIONS_MODULE_INFO_TITLE = 'About organizations';
export const ORGANIZATIONS_MODULE_INFO_MARKDOWN = `
## What the organizations module is

The organizations module lets a user view the organizations they belong to and switch which one is currently active in the dashboard.

## Why active organization matters

Hana dashboard data is organization-scoped. The active organization determines which memories, spaces, users, tasks, prompts, and settings you are looking at.

## What this module helps you manage

- Seeing the current active organization.
- Switching to another organization you belong to.
- Starting flows to create a new organization or join one.
- Reviewing plan and seat context across memberships.

## What this module does not do

- It does not merge data from multiple organizations.
- It does not edit all organization settings from this page.
- It does not override role permissions inside the selected organization.

## When to use organizations

Use this module whenever the dashboard looks like it is showing the wrong org context, or when you need to switch into a different organization before continuing work.
`;

export const ACTIVE_ORGANIZATION_INFO_TITLE = 'Current active organization';
export const ACTIVE_ORGANIZATION_INFO_MARKDOWN = `
## Why this section matters

This section shows the single organization that currently scopes the dashboard.

## What to check here

- The display name matches the org you intend to work in.
- The plan and seat data match expectations.
- You are not about to make edits in the wrong organization context.
`;

export const ORGANIZATION_MEMBERSHIPS_INFO_TITLE =
  'All organization memberships';
export const ORGANIZATION_MEMBERSHIPS_INFO_MARKDOWN = `
## What this section shows

This section lists the organizations you belong to and lets you switch which one is active.

## What to do here

- Mark another organization active before opening modules that should operate in that org.
- Start the join or create flow when the organization you need is not listed.
- Review your role and active status if access looks different than expected.
`;

export const HELP_MODULE_INFO_TITLE = 'About help';
export const HELP_MODULE_INFO_MARKDOWN = `
## What the help module is

The help module is the dashboard support hub for onboarding, product guidance, docs, videos, and support routes.

## What this module helps you do

- Start or restart the dashboard tour.
- Open documentation and tutorial content.
- Reach support and provide feedback.
- Discover external learning resources such as video walkthroughs.

## When to use help

Use this module when you are unsure how a feature works, want to onboard a new teammate faster, or need a support path for something that is not clear from the product UI alone.
`;

export const FILE_PICKER_MODULE_INFO_TITLE = 'About file picker';
export const FILE_PICKER_MODULE_INFO_MARKDOWN = `
## What the file picker module is

The file picker module lets users grant Hana access to selected Drive files and review previously shared files.

## What this module helps you manage

- Granting access to a file.
- Reviewing which files you already shared.
- Revoking or restoring file access.
- Seeing organization-level shared files when your role allows it.

## What this module does not do

- It does not ingest every file automatically just because it exists in Drive.
- It does not replace deeper memory workflows when structured ingestion is required.
- It does not grant access outside the permissions of the connected account.

## When to use file picker

Use this module when a specific Drive file should be made available to Hana without running a broader ingestion workflow first.
`;

export const FILE_PICKER_ACCESS_INFO_TITLE = 'Managing file access';
export const FILE_PICKER_ACCESS_INFO_MARKDOWN = `
## What to do here

- Use the picker to grant Hana access to a specific file.
- Use the tabs to switch between your files and org files when permitted.
- Revoke access when a file should no longer be available to Hana.
- Restore access when a previously revoked file should be usable again.
`;

export const JIRA_CONNECTOR_INFO_TITLE = 'About the Jira connector';
export const JIRA_CONNECTOR_INFO_MARKDOWN = `
## What this page is for

This page manages Jira accounts connected to the current organization.

## What you need before connecting

- A valid Jira instance URL.
- An API token for the Jira account.
- The email address for that Jira account.

## What happens after connecting

- The connector becomes available to memory and workflow features that depend on Jira.
- Admins can review who connected it and when.
- You can disconnect it later if the configuration is no longer needed.

## When to use this page

Use it when the organization wants Hana to work with Jira-backed project data.
`;

export const CONFLUENCE_CONNECTOR_INFO_TITLE = 'About the Confluence connector';
export const CONFLUENCE_CONNECTOR_INFO_MARKDOWN = `
## What this page is for

This page manages Confluence accounts connected to the current organization.

## What you need before connecting

- A valid Confluence instance URL.
- An API token for the account.
- The email address for that Confluence account.

## What happens after connecting

- Confluence can be used as a knowledge source for Hana.
- The organization can audit configured connections from this page.
- You can disconnect the connector if it should no longer be used.

## When to use this page

Use it when Hana should retrieve or ingest knowledge from Confluence content.
`;

export const NOTION_CONNECTOR_INFO_TITLE = 'About the Notion connector';
export const NOTION_CONNECTOR_INFO_MARKDOWN = `
## What this page is for

This page explains the Notion connection flow for the current organization.

## What to expect

- The connector setup controls live here when the organization enables Notion support.
- You should use this page to understand the connection requirements before expecting Notion content to appear elsewhere.

## When to use this page

Use it when your team wants Hana to work with knowledge stored in Notion.
`;

export const ASANA_CONNECTOR_INFO_TITLE = 'About the Asana connector';
export const ASANA_CONNECTOR_INFO_MARKDOWN = `
## What this page is for

This page manages Asana accounts connected to the current organization.

## What you need before connecting

- An Asana account that can authorize the required workspace access.
- Permission to connect Asana for the current organization.
- The target workspace and projects you expect Hana to work with after connection.

## What happens after connecting

- The connector becomes available to Hana workflows that depend on Asana access.
- Admins can review existing Asana connections from this page.
- You can disconnect the account later if the organization should stop using it.

## When to use this page

Use it when the organization wants Hana to operate with Asana-backed project data or workflows.
`;

export const WHATS_HAPPENING_MODULE_INFO_TITLE = "About What's Happening";
export const WHATS_HAPPENING_MODULE_INFO_MARKDOWN = `
## What the What's Happening module is

What's Happening is Hana's digest and summary control center for recent work signals, reminders, blockers, and themes pulled from the active organization context.

## What this module helps you manage

- Reading generated daily and weekly summaries.
- Searching across the generated digest content.
- Enabling or pausing recurring digest emails where entitled.
- Reviewing action items, blockers, reminders, and themes without opening every source module first.

## How to use this module well

- Use daily mode for near-term operational awareness.
- Use weekly mode for broader pattern review.
- Treat the digest as a prioritization layer, then drill into source modules for edits.
- Keep schedule and recipient settings aligned with the people who actually need the summary.

## What this module does not do

- It does not replace the original source data in Tasks, Spaces, or other modules.
- It does not guarantee every operational detail is captured if upstream coverage is incomplete.
- It does not bypass plan restrictions on recurring digests.

## When to use What's Happening

Use this module when you want a fast summary of recent organizational activity and follow-up signals instead of manually checking multiple modules.
`;

export const MEETING_OVERVIEW_MODULE_INFO_TITLE = 'About meeting overview';
export const MEETING_OVERVIEW_MODULE_INFO_MARKDOWN = `
## What the meeting overview module is

Meeting Overview lists recent Google Meet sessions and helps teams capture meeting transcripts into Hana memory when the plan and permissions allow it.

## What this module helps you do

- Review recent meetings in one place.
- Check meeting metadata and transcript availability.
- Start transcript ingestion into memory for later retrieval.
- Confirm whether access scopes or plan level are blocking ingestion.

## What this module does not do

- It does not replace Google Meet as the live meeting surface.
- It does not create transcripts if they were never available upstream.
- It does not guarantee ingestibility when scopes, plan, or source data are missing.
`;

export const CRM_MODULE_INFO_TITLE = 'About CRM';
export const CRM_MODULE_INFO_MARKDOWN = `
## What the CRM module is

CRM is an internal operations workspace for Hanabi teams. It groups admin and internal business workflows that do not belong in the standard customer-facing dashboard modules.

## What this module helps with

- Accessing internal-only admin tools and workflows.
- Switching between CRM submodules without leaving the dashboard shell.
- Operating business and support flows that are intentionally separated from customer org features.
`;

export const INVOCATION_HISTORY_MODULE_INFO_TITLE = 'About invocation history';
export const INVOCATION_HISTORY_MODULE_INFO_MARKDOWN = `
## What invocation history is

Invocation history records Hana interactions so teams can inspect what was asked, what context was used, and what Hana returned.

## What this module helps you do

- Search prior invocations.
- Inspect prompt and response pairs.
- Audit organization and user-specific interactions.
- Investigate support issues, quality concerns, or behavior regressions.

## What this module does not do

- It does not change the original invocation after it happened.
- It does not replace the primary user-facing conversation surfaces.
`;

export const SENT_PROMPTS_MODULE_INFO_TITLE = 'About sent prompts';
export const SENT_PROMPTS_MODULE_INFO_MARKDOWN = `
## What sent prompts are

Sent prompts are records of prompts that were actually constructed and sent through Hana flows, along with response and feedback metadata.

## What this module helps you do

- Review historical prompt usage.
- Filter by date, organization, user, and feedback state.
- Inspect individual sent prompt records in detail.
- Audit prompt quality and downstream response behavior.
`;

export const MEMORY_DETAILS_MODULE_INFO_TITLE = 'About memory details';
export const MEMORY_DETAILS_MODULE_INFO_MARKDOWN = `
## What the memory details page is

Memory Details is the canonical detail page for one memory batch.

## What this page helps you do

- Review source metadata and ingestion status.
- Inspect recent batch events and processing history.
- Understand what source type the batch came from and how it was configured.
- Navigate to lower-level data such as chunks or extracted rows when relevant.
`;

export const MEMORY_PAGE_DATA_INFO_TITLE = 'About memory page data';
export const MEMORY_PAGE_DATA_INFO_MARKDOWN = `
## What the memory page data page is

Memory Page Data shows extracted row or sheet-level data for memory batches that ingest structured page content.

## What this page helps you do

- Inspect the text extracted from each row or page.
- Verify sheet titles and row indices.
- Audit whether the structured source was parsed as expected.
`;

export const MEMORY_CHUNKS_MODULE_INFO_TITLE = 'About memory chunks';
export const MEMORY_CHUNKS_MODULE_INFO_MARKDOWN = `
## What the memory chunks page is

Memory Chunks shows the chunked content Hana uses for retrieval from a selected memory batch.

## What this page helps you do

- Search the chunk content for a specific batch.
- Review the latest batch events for ingestion troubleshooting.
- Verify that the chunked representation matches the expected source content.
`;

export const GLOBAL_MEMORY_SEARCH_INFO_TITLE = 'About global memory search';
export const GLOBAL_MEMORY_SEARCH_INFO_MARKDOWN = `
## What global memory search is

Global Memory Search lets you search across memory content without opening one batch at a time.

## What this page helps you do

- Find relevant stored knowledge quickly.
- Inspect matching memory snippets.
- Update or review returned records when search exposes stale or weak content.
`;

export const BUY_PLAN_MODULE_INFO_TITLE = 'About buying a plan';
export const BUY_PLAN_MODULE_INFO_MARKDOWN = `
## What this page is for

Buy Plan is the checkout and subscription change page for upgrading, changing seats, or resuming paid billing for the active organization.

## What this page helps you do

- Choose seat count and billing interval.
- Apply discount codes when eligible.
- Start checkout or update an existing subscription.
- Review the effect of plan changes before confirming purchase.
`;

export const ALL_PLANS_MODULE_INFO_TITLE = 'About plans and pricing';
export const ALL_PLANS_MODULE_INFO_MARKDOWN = `
## What the plans page is

This page compares Hana plans, capabilities, and commercial differences for the active organization.

## What this page helps you do

- Compare plan features and limits.
- Understand upgrade tradeoffs.
- Review seats, billing intervals, and commercial fit before purchase.
`;

export const CHUNK_BATCH_LOOKUP_INFO_TITLE = 'About chunk batch lookup';
export const CHUNK_BATCH_LOOKUP_INFO_MARKDOWN = `
## What this page is for

Chunk Batch Lookup is a super admin tool for finding memory batch records by batch id.

## What this page helps you do

- Search for a specific batch quickly.
- Inspect associated memory, status, organization, and audit fields.
- Troubleshoot ingestion issues when a concrete batch id is already known.
`;

export const PRE_RELEASE_TESTS_INFO_TITLE = 'About pre-release tests';
export const PRE_RELEASE_TESTS_INFO_MARKDOWN = `
## What the pre-release tests page is

Pre-release Tests is an internal validation workspace for running curated eval and regression checks before shipping changes.

## What this page helps you do

- Review the eval catalog.
- Run scenario-based validation flows.
- Inspect expected prompts, assertions, and target users before release.
`;

export const MEMORY_MAINTENANCE_INFO_TITLE = 'About memory maintenance';
export const MEMORY_MAINTENANCE_INFO_MARKDOWN = `
## What the memory maintenance page is

Memory Maintenance is a super admin utility page for operational cleanup tasks on memory data.

## What this page helps you do

- Run deduplication safely.
- Use dry-run mode before mutating data.
- Execute cleanup workflows that should stay restricted to maintenance operators.
`;

export const INVOKE_AS_USER_INFO_TITLE = 'About invoke as user';
export const INVOKE_AS_USER_INFO_MARKDOWN = `
## What this page is for

Invoke as User is an internal admin tool for sending a message through Hana as a selected user context.

## What this page helps you do

- Reproduce user-specific behavior.
- Inspect how permissions or user context affect invocation outcomes.
- Debug workflows without asking the end user to replay the issue themselves.
`;

export const SEARCH_USERS_INFO_TITLE = 'About user search';
export const SEARCH_USERS_INFO_MARKDOWN = `
## What this page is for

Search Users is a super admin lookup tool for finding users by id, name, or email.

## What this page helps you do

- Locate users quickly for support or debugging.
- Inspect core identity and organization context.
- Reduce guesswork before using deeper admin tools.
`;

export const SEARCH_ORGANIZATIONS_INFO_TITLE = 'About organization search';
export const SEARCH_ORGANIZATIONS_INFO_MARKDOWN = `
## What this page is for

Search Organizations is a super admin lookup tool for finding organizations by id, name, or website.

## What this page helps you do

- Locate organizations quickly for support work.
- Verify organization identity before using admin-only actions.
- Reduce debugging time when the org context is uncertain.
`;

export const TAGS_MODULE_INFO_TITLE = 'About tags';
export const TAGS_MODULE_INFO_MARKDOWN = `
## What the tags module is

Tags are reusable labels for organizing Hana records such as memories and tasks.

## What this module helps you do

- Create new tags.
- Keep naming consistent across the organization.
- Review and manage the shared tag set used in filtering and organization flows.
`;

export const POLLS_MODULE_INFO_TITLE = 'About polls';
export const POLLS_MODULE_INFO_MARKDOWN = `
## What the polls module is

The polls module is the dashboard control surface for Google Chat polls that Hana can read, review, and manage from the active organization context.

## What this module helps you do

- Review saved poll topics, descriptions, and choices.
- Inspect poll state, visibility mode, and voting totals.
- See visible-vote breakdowns when the poll was created as non-anonymous.
- Edit, close, reopen, or duplicate eligible polls from one place.

## How to use this module well

- Use it as the historical audit view for polls after they were created in chat.
- Treat anonymous polls as aggregate-only by design.
- Refresh the page when you need the latest response totals after live activity.

## What this module does not do

- It does not reveal voter identity for anonymous polls.
- It does not replace the live Google Chat surface where people cast votes.
- It does not infer poll intent if the original topic and choices are unclear.
`;

export const SPRINTS_MODULE_INFO_TITLE = 'About sprint management';
export const SPRINTS_MODULE_INFO_MARKDOWN = `
## What the sprint management page is

Sprint Management is the planning surface for grouping tasks into fixed time windows so teams can track capacity, focus, and progress.

## What this page helps you do

- Create and manage sprints.
- Review planned, active, completed, and archived sprint totals.
- Search and filter the sprint list.
- Open a sprint board to review the work assigned to it.

## What this page does not do

- It does not replace task-level execution details.
- It does not automatically assign the right work to a sprint without deliberate planning.
`;

export const SPRINT_BOARD_MODULE_INFO_TITLE = 'About sprint board';
export const SPRINT_BOARD_MODULE_INFO_MARKDOWN = `
## What the sprint board page is

The sprint board shows the tasks assigned to one sprint along with capacity, progress, and workload signals.

## What this page helps you do

- Review sprint progress and total story points.
- Add work from backlog or remove selected work.
- Filter sprint tasks by search and assignee.
- Inspect auto-manage settings where enabled.

## What this page does not do

- It does not replace the main tasks module for cross-sprint workflow management.
- It does not automatically fix over-capacity or stale tasks without user action.
`;

export const SPRINT_BACKLOG_MODULE_INFO_TITLE = 'About sprint backlog';
export const SPRINT_BACKLOG_MODULE_INFO_MARKDOWN = `
## What the sprint backlog page is

Sprint Backlog shows candidate tasks that are not yet assigned to a sprint so you can add them into the current sprint deliberately.

## What this page helps you do

- Search the available backlog.
- Select multiple tasks for assignment.
- Inspect task details before adding them to a sprint.

## What this page does not do

- It does not replace the main backlog view in the tasks module.
- It does not automatically decide which tasks belong in the sprint.
`;

type ModuleInfoDefinition = {
  title: string;
  markdownContent: string;
};

const normalizeModuleHeaderTitle = (title: string): string =>
  title.trim().split(' - ')[0].replace(/\s+/g, ' ').toLowerCase();

const MODULE_INFO_BY_TITLE_MATCH: Array<{
  matches: string[];
  definition: ModuleInfoDefinition;
}> = [
  {
    matches: ["what's happening", 'whats happening'],
    definition: {
      title: WHATS_HAPPENING_MODULE_INFO_TITLE,
      markdownContent: WHATS_HAPPENING_MODULE_INFO_MARKDOWN,
    },
  },
  {
    matches: ['meeting overview'],
    definition: {
      title: MEETING_OVERVIEW_MODULE_INFO_TITLE,
      markdownContent: MEETING_OVERVIEW_MODULE_INFO_MARKDOWN,
    },
  },
  {
    matches: ['customer relationship management'],
    definition: {
      title: CRM_MODULE_INFO_TITLE,
      markdownContent: CRM_MODULE_INFO_MARKDOWN,
    },
  },
  {
    matches: ['invocation history'],
    definition: {
      title: INVOCATION_HISTORY_MODULE_INFO_TITLE,
      markdownContent: INVOCATION_HISTORY_MODULE_INFO_MARKDOWN,
    },
  },
  {
    matches: ['sent prompt', 'sent prompts'],
    definition: {
      title: SENT_PROMPTS_MODULE_INFO_TITLE,
      markdownContent: SENT_PROMPTS_MODULE_INFO_MARKDOWN,
    },
  },
  {
    matches: ['memory details'],
    definition: {
      title: MEMORY_DETAILS_MODULE_INFO_TITLE,
      markdownContent: MEMORY_DETAILS_MODULE_INFO_MARKDOWN,
    },
  },
  {
    matches: ['memory page data'],
    definition: {
      title: MEMORY_PAGE_DATA_INFO_TITLE,
      markdownContent: MEMORY_PAGE_DATA_INFO_MARKDOWN,
    },
  },
  {
    matches: ['memory chunks'],
    definition: {
      title: MEMORY_CHUNKS_MODULE_INFO_TITLE,
      markdownContent: MEMORY_CHUNKS_MODULE_INFO_MARKDOWN,
    },
  },
  {
    matches: ['global memory search'],
    definition: {
      title: GLOBAL_MEMORY_SEARCH_INFO_TITLE,
      markdownContent: GLOBAL_MEMORY_SEARCH_INFO_MARKDOWN,
    },
  },
  {
    matches: ['buy plan'],
    definition: {
      title: BUY_PLAN_MODULE_INFO_TITLE,
      markdownContent: BUY_PLAN_MODULE_INFO_MARKDOWN,
    },
  },
  {
    matches: ['all plans'],
    definition: {
      title: ALL_PLANS_MODULE_INFO_TITLE,
      markdownContent: ALL_PLANS_MODULE_INFO_MARKDOWN,
    },
  },
  {
    matches: ['chunk batch id'],
    definition: {
      title: CHUNK_BATCH_LOOKUP_INFO_TITLE,
      markdownContent: CHUNK_BATCH_LOOKUP_INFO_MARKDOWN,
    },
  },
  {
    matches: ['pre release tests'],
    definition: {
      title: PRE_RELEASE_TESTS_INFO_TITLE,
      markdownContent: PRE_RELEASE_TESTS_INFO_MARKDOWN,
    },
  },
  {
    matches: ['memory maintenance'],
    definition: {
      title: MEMORY_MAINTENANCE_INFO_TITLE,
      markdownContent: MEMORY_MAINTENANCE_INFO_MARKDOWN,
    },
  },
  {
    matches: ['invoke as user'],
    definition: {
      title: INVOKE_AS_USER_INFO_TITLE,
      markdownContent: INVOKE_AS_USER_INFO_MARKDOWN,
    },
  },
  {
    matches: ['search users'],
    definition: {
      title: SEARCH_USERS_INFO_TITLE,
      markdownContent: SEARCH_USERS_INFO_MARKDOWN,
    },
  },
  {
    matches: ['search organizations', 'search organization'],
    definition: {
      title: SEARCH_ORGANIZATIONS_INFO_TITLE,
      markdownContent: SEARCH_ORGANIZATIONS_INFO_MARKDOWN,
    },
  },
  {
    matches: ['tags'],
    definition: {
      title: TAGS_MODULE_INFO_TITLE,
      markdownContent: TAGS_MODULE_INFO_MARKDOWN,
    },
  },
  {
    matches: ['polls'],
    definition: {
      title: POLLS_MODULE_INFO_TITLE,
      markdownContent: POLLS_MODULE_INFO_MARKDOWN,
    },
  },
  {
    matches: ['sprint management'],
    definition: {
      title: SPRINTS_MODULE_INFO_TITLE,
      markdownContent: SPRINTS_MODULE_INFO_MARKDOWN,
    },
  },
  {
    matches: ['sprint board'],
    definition: {
      title: SPRINT_BOARD_MODULE_INFO_TITLE,
      markdownContent: SPRINT_BOARD_MODULE_INFO_MARKDOWN,
    },
  },
  {
    matches: ['sprint backlog'],
    definition: {
      title: SPRINT_BACKLOG_MODULE_INFO_TITLE,
      markdownContent: SPRINT_BACKLOG_MODULE_INFO_MARKDOWN,
    },
  },
];

export const resolveModuleHeaderInfoByTitle = (
  title: string,
): ModuleInfoDefinition | null => {
  const normalizedTitle = normalizeModuleHeaderTitle(title);
  const matchedRule = MODULE_INFO_BY_TITLE_MATCH.find((rule) =>
    rule.matches.some((match) => normalizedTitle.includes(match)),
  );

  return matchedRule?.definition ?? null;
};
